import React from 'react'
import { Popover, Grid, Divider, Button } from '@mui/material'
import { useTheme } from '@mui/material/styles'
import RequiredExperienceSlider from 'components/RequiredExperienceSlider'
import AutoCompleteChipInput from 'components/AutoCompleteChipInput'
import Box from 'components/Box'
import { getSkillLabel, SkillEntry } from 'services/jobSkills'
import { useSkillsAutocomplete } from 'hooks/useSkillAutocomplete'
import { IAutoCompleteOption } from 'components/AutoCompleteTextField/types'

export interface ISkillsInputPopoverProps {
  popoverAnchor: HTMLElement | null
  skill: SkillEntry | null
  requiredSkills: boolean
  onClose: () => void
  onChange: (skill: SkillEntry) => void
}

function SkillsInputPopover(props: ISkillsInputPopoverProps) {
  const { popoverAnchor, skill, requiredSkills, onChange, onClose } = props
  const theme = useTheme()
  const popoverOpen = Boolean(popoverAnchor && skill)
  const skillsAutocomplete = useSkillsAutocomplete()

  const handleRequiredYearsChange = React.useCallback((value: number) => {
    if (!skill) return

    onChange({
      ...skill,
      required_years: value,
    })
  }, [skill, onChange])

  const handleKeyDown = React.useCallback((e: React.KeyboardEvent<any>) => {
    if (e.key === 'Enter') {
      e.stopPropagation()
      e.preventDefault()
      onClose()
    }
  }, [onClose])

  const blockEnterKeyEvent = React.useCallback((e: React.KeyboardEvent<any>) => {
    if (e.key === 'Enter') {
      e.stopPropagation()
      e.preventDefault()
    }
  }, [])

  const handleAltSkillRequiredYearsChange = (targetSkill: SkillEntry, value: number) => {
    if (skill?.group_additional_skills) {
      const newAdditionalSkills = skill.group_additional_skills.slice()
      const targetSkillIndex = newAdditionalSkills.findIndex(altSkill => altSkill.skill_id === targetSkill.skill_id)

      if (targetSkillIndex !== -1) {
        newAdditionalSkills.splice(targetSkillIndex, 1, {
          ...targetSkill,
          required_years: value,
        })
      }

      onChange({
        ...skill,
        group_additional_skills: newAdditionalSkills,
      })
    }
  }

  const handleAltSkillsChange = (value: IAutoCompleteOption<string>[]) => {
    if (!skill) return

    onChange({
      ...skill,
      group_additional_skills: value.map(altSkill => ({ ...altSkill, skill_id: altSkill.value, name: altSkill.label, required: requiredSkills })),
    })
  }

  return (
    <Popover
      open={popoverOpen}
      anchorEl={popoverAnchor}
      onClose={onClose}
      anchorOrigin={{
        vertical: 'bottom',
        horizontal: 'left',
      }}
      transformOrigin={{
        vertical: 'top',
        horizontal: 'left',
      }}
      onKeyDown={handleKeyDown}
      onKeyUp={blockEnterKeyEvent}
      onKeyPress={blockEnterKeyEvent}
    >
      <div style={{ width: 360, maxWidth: '100%' }} data-cy="skills-input-popover">
        <Box variant="compact">
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <RequiredExperienceSlider
                style={{ marginBottom: theme.spacing(-1) }}
                skill={{ id: skill?.skill_id ?? '', name: skill?.name ?? '', requiredYears: skill?.required_years ?? 0 }}
                onChange={handleRequiredYearsChange}
              />
            </Grid>

            {skill?.group_additional_skills?.map(popoverAltSkill => (
              <Grid item xs={12} key={popoverAltSkill.skill_id}>
                <RequiredExperienceSlider
                  style={{ marginBottom: theme.spacing(-1) }}
                  skill={{ id: popoverAltSkill.skill_id, name: popoverAltSkill.name, requiredYears: popoverAltSkill.required_years ?? 0 }}
                  onChange={value => handleAltSkillRequiredYearsChange(popoverAltSkill, value)}
                />
              </Grid>
            ))}
          </Grid>
        </Box>

        <Divider style={{ margin: 0, marginBottom: theme.spacing(1) }} />

        <Box variant="compact">
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <div onKeyDown={blockEnterKeyEvent} onKeyUp={blockEnterKeyEvent} onKeyPress={blockEnterKeyEvent} role="none">
                <AutoCompleteChipInput
                  {...skillsAutocomplete}
                  suggestions={skillsAutocomplete.suggestions?.filter(s => s.value !== skill?.skill_id)} // exclude "main" skill
                  label="Acceptable alternatives"
                  fullWidth
                  freeSolo
                  value={(skill?.group_additional_skills || []).map(s => ({ value: s.skill_id, label: getSkillLabel(s) }))}
                  onChange={handleAltSkillsChange}
                />
              </div>
            </Grid>

            <Grid item xs={12} style={{ textAlign: 'right' }}>
              <Button color="secondary" onClick={onClose} data-cy="skills-input-popover-done">
                Done
              </Button>
            </Grid>
          </Grid>
        </Box>
      </div>
    </Popover>
  )
}

export default React.memo(SkillsInputPopover)
